import React from 'react';
import { MapPin, Building2 } from 'lucide-react';

interface RegionLite { _id: string; regionId: number | string; name: string }
interface OfficeLite { _id: string; officeNo: number | string; name: string; regionId?: any }

interface Props {
  regions: RegionLite[];
  offices: OfficeLite[];
  selectedRegionIds: string[];
  selectedOfficeIds: string[];
  onChangeRegions: (ids: string[]) => void;
  onChangeOffices: (ids: string[]) => void;
}

const OfficeRegionFilter: React.FC<Props> = ({
  regions, offices,
  selectedRegionIds, selectedOfficeIds,
  onChangeRegions, onChangeOffices,
}) => {
  const toggle = (id: string, arr: string[], set: (a: string[]) => void) =>
    set(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);

  const filteredOffices = selectedRegionIds.length > 0
    ? offices.filter(o => {
        const rid = typeof o.regionId === 'object' && o.regionId ? String(o.regionId._id) : String(o.regionId);
        return selectedRegionIds.includes(rid);
      })
    : offices;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <MapPin className="h-4 w-4 text-primary-500" /> Regions
          </label>
          {selectedRegionIds.length > 0 && (
            <button
              onClick={() => onChangeRegions([])}
              className="text-[11px] text-primary-600 dark:text-primary-400 hover:underline"
            >Clear</button>
          )}
        </div>
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-40 overflow-auto divide-y divide-gray-100 dark:divide-gray-700/50 bg-white dark:bg-gray-800">
          {regions.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-3">No regions available</p>
          )}
          {regions.map(r => (
            <label key={r._id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 text-sm">
              <input
                type="checkbox"
                className="rounded border-gray-300 accent-primary-600"
                checked={selectedRegionIds.includes(r._id)}
                onChange={() => toggle(r._id, selectedRegionIds, onChangeRegions)}
              />
              <span className="text-gray-700 dark:text-gray-300">
                <span className="font-medium text-primary-700 dark:text-primary-400">R-{r.regionId}</span> · {r.name}
              </span>
            </label>
          ))}
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          {selectedRegionIds.length === 0 ? 'All regions' : `${selectedRegionIds.length} region(s) selected`}
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Building2 className="h-4 w-4 text-primary-500" /> Offices
          </label>
          {selectedOfficeIds.length > 0 && (
            <button
              onClick={() => onChangeOffices([])}
              className="text-[11px] text-primary-600 dark:text-primary-400 hover:underline"
            >Clear</button>
          )}
        </div>
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-40 overflow-auto divide-y divide-gray-100 dark:divide-gray-700/50 bg-white dark:bg-gray-800">
          {filteredOffices.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-3">No offices available</p>
          )}
          {filteredOffices.map(o => (
            <label key={o._id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 text-sm">
              <input
                type="checkbox"
                className="rounded border-gray-300 accent-primary-600"
                checked={selectedOfficeIds.includes(o._id)}
                onChange={() => toggle(o._id, selectedOfficeIds, onChangeOffices)}
              />
              <span className="text-gray-700 dark:text-gray-300">
                <span className="font-medium text-primary-700 dark:text-primary-400">#{o.officeNo}</span> · {o.name}
              </span>
            </label>
          ))}
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          {selectedOfficeIds.length === 0 ? 'All offices' : `${selectedOfficeIds.length} office(s) selected`}
          {selectedRegionIds.length > 0 && ' (filtered by region)'}
        </p>
      </div>
    </div>
  );
};

export default OfficeRegionFilter;
